import { create } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';
import CryptoJS from 'crypto-js';
import { useEffect, useState } from 'react';
import { User } from '@components/axiosInstances/types/user-type';

const SECRET_KEY = process.env.NEXT_PUBLIC_SECRET_KEY || 'qa4xp8fXR0ZmMGxmIAbqMC1jF6O17PisWCyXIQhEq'
const STORE_NAME = 'auth-secure-store';

const encryptData = (data: any) => {
    return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
};

const decryptData = (ciphertext: string) => {
    try {
        const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
        return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (error) {
        console.error('Decryption error:', error);
        return null;
    }
};

// Define Zustand Store with hydration handling
type AuthState = {
    user: User | null;
    token: string | null;
    fingerprint: string | null;
    initialized: boolean;
    setUser: (user: User | null) => void;
    setToken: (token: string | null) => void;
    setFingerprint: (fingerprint: string | null) => void;
    logout: () => void;
};

export const useAuthStore = create<AuthState>()(
    persist((set) => ({
        user: null,
        token: null,
        fingerprint: null,
        initialized: false,
        setUser: (user) => set(() => ({ user, initialized: true })),
        setToken: (token) => set(() => ({ token, initialized: true })),
        setFingerprint: (fingerprint) => set(() => ({ fingerprint, initialized: true })),
        logout: () => {
            if (typeof window !== 'undefined') {
                document.cookie = `auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
                document.cookie = `auth_fingerprint=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
            }
            localStorage.removeItem(STORE_NAME); // Clear stored values
            set(() => ({ user: null, token: null, initialized: false }));
        },
    }),
        {
            name: STORE_NAME,
            storage: {
                getItem: (key) => {
                    if (typeof window === 'undefined') return null; // Prevent SSR issues
                    const data = localStorage.getItem(key);
                    const decrypted = data ? decryptData(data) : null;
                    const token = decrypted?.state?.token;
                    const fingerprint = decrypted?.state?.fingerprint;
                    if (token) {
                        document.cookie = `auth_token=${encodeURIComponent(token)}; path=/; max-age=604800; SameSite=Lax`;
                    }
                    if (fingerprint) {
                        document.cookie = `auth_fingerprint=${encodeURIComponent(fingerprint)}; path=/; max-age=604800; SameSite=Lax`;
                    }
                    return decrypted;
                },
                setItem: (key, value: any) => {
                    if (typeof window !== 'undefined') {
                        localStorage.setItem(key, encryptData(value));
                        const token = value?.state?.token;
                        const fingerprint = value?.state?.fingerprint;
                        if (token) {
                            document.cookie = `auth_token=${encodeURIComponent(token)}; path=/; max-age=604800; SameSite=Lax`;
                        } else {
                            document.cookie = `auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
                        }
                        if (fingerprint) {
                            document.cookie = `auth_fingerprint=${encodeURIComponent(fingerprint)}; path=/; max-age=604800; SameSite=Lax`;
                        } else {
                            document.cookie = `auth_fingerprint=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
                        }
                    }
                },
                removeItem: (key) => {
                    if (typeof window !== 'undefined') {
                        localStorage.removeItem(key);
                        document.cookie = `auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
                        document.cookie = `auth_fingerprint=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
                    }
                },
            },
        } as PersistOptions<AuthState>
    )
);

// ─── Cross-Tab Sync ───────────────────────────────────────────────────────────
// Browser menembak event `storage` di semua tab LAIN (bukan tab yang mengubah)
// setiap kali localStorage berubah. Kita manfaatkan ini untuk rehydrate store
// sehingga tab yang idle otomatis mendapat token terbaru setelah role diubah
// di tab lain — tanpa perlu reload halaman.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event: StorageEvent) => {
        if (event.key === STORE_NAME) {
            // Jika key dihapus (logout dari tab lain), paksa logout di tab ini juga
            if (event.newValue === null) {
                useAuthStore.setState({ user: null, token: null, initialized: false });
                return;
            }
            // Rehydrate dari localStorage yang sudah diupdate oleh tab lain
            useAuthStore.persist.rehydrate();
        }
    });
}

// ─── Custom Hook to Ensure Zustand Hydration Completes ───────────────────────
export const useAuthGuard = () => {
    const { user, token, initialized } = useAuthStore();
    const [hydrated, setHydrated] = useState(false);

    useEffect(() => {
        setHydrated(true);
        // only if session is dev
        if (process.env.NODE_ENV !== "production") {
            window.sessionAuth = { user, token }
        }
    }, [user, token, initialized]);

    return { isAuthenticated: !!user, user, token, hydrated, initialized };
};


export default useAuthStore;